import React, { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { StoryGameImage1, StoryGameImage2 } from 'public/assets';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import theme from '@/styles/theme';
import useTranslation from 'next-translate/useTranslation';

const containerStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
`;

const backgroundStyle = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const imageContainerStyle = css`
  flex: 1;
  position: relative;
  overflow: hidden;
  aspect-ratio: 10 / 7;
`;

const imageStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const overlayStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const buttonTextStyle = css`
  height: 30px;
  background: #753bbd;
  width: 100%;
  z-index: 1;
  font-size: 12px;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border-radius: 0 0 8px 8px;

  @media (max-width: ${theme.devices.md}px) {
    height: auto;
    padding: 5.4px 0px 4.6px 0px;
  }
`;

const purpleLineStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 2px;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    #fff 15%,
    #fff 85%,
    rgba(255, 255, 255, 0) 100%
  );
  box-shadow: 0px 0px 24px 10px #9e7cff, 0px 4px 4px 0px #9642ff;
`;

const StoryGameAiImageButton: React.FC = () => {
  const [progress, setProgress] = useState<number>(100);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(100);
  const { isPC } = useWindowWidth();
  const { t } = useTranslation();

  useEffect(() => {
    let resizeObserver: ResizeObserver;

    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
      }
    };

    // ResizeObserver를 사용하여 크기 변화 감지
    if (containerRef.current) {
      resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(containerRef.current);
    }

    // 초기 너비 측정을 위한 약간의 지연
    const initialMeasurementTimeout = setTimeout(updateWidth, 0);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      clearTimeout(initialMeasurementTimeout);
    };
  }, []);

  useEffect(() => {
    const animate = () => {
      setProgress((prev) => (prev === 0 ? 100 : 0));
    };
    // 즉시 첫 번째 애니메이션 실행
    animate();
    const timer = setInterval(animate, 1600); // 3초마다 애니메이션 토글

    return () => clearInterval(timer);
  }, []);

  const clipStyle = css`
    clip-path: inset(0 ${100 - progress}% 0 0);
    transition: clip-path 1.25s ease-in-out;
  `;

  const lineStyle = css`
    transform: translateX(${(progress * (containerWidth - 2)) / 100}px);
    transition: transform 1.25s ease-in-out;
  `;

  return (
    <div ref={containerRef} css={containerStyle}>
      <div css={backgroundStyle}>
        <div css={imageContainerStyle}>
          <img
            css={imageStyle}
            src={StoryGameImage1.src}
            alt="Story Game Image 1"
          />
          <div css={[overlayStyle, clipStyle]}>
            <img
              css={imageStyle}
              src={StoryGameImage2.src}
              alt="Story Game Image 2"
            />
          </div>
        </div>
        <div css={buttonTextStyle}>
          {isPC ? (
            t('storygame_ai_onboarding_screen_button_create_profile')
          ) : (
            <div style={{ justifyContent: 'center', alignItems: 'center' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="11"
                viewBox="1 0 12 11"
                fill="none"
              >
                <path
                  d="M6 2.6001V10.6001"
                  stroke="white"
                  strokeLinecap="round"
                />
                <path
                  d="M10 6.6001L2 6.6001"
                  stroke="white"
                  strokeLinecap="round"
                />
              </svg>
              {t('storygame_ai_onboarding_screen_button_upload_selfie')}
            </div>
          )}
        </div>
      </div>
      <div css={[purpleLineStyle, lineStyle]}></div>
    </div>
  );
};

export default StoryGameAiImageButton;
